import { memo, useCallback, useState } from "react";
import { ISalesContractProps } from "../../SalesContractHistories/SalesContractHistoryForm/models";
import { useParams } from "react-router-dom";
import { useSalesContract } from "../../../../../store/contexts/SalesContractContext";
import { useTranslation } from "react-i18next";
import { FormContainer, Treturn, useFetch } from "@4uhub/lib4uhub";
import {
  createSalesContractPayer,
  findSalesContractPayerById,
  updateSalesContractPayer,
} from "../../../../../services/salesContractPayer.service";
import { Grid } from "@mui/material";
import { ISalesContractPayer } from "../../../../../models/sales-contract-payer";
import {
  salesContractPayerSchema,
  TSalesContractPayerForm,
} from "./salesContractPayerSchema";
import { SalesContractPayerForm } from "./SalesContractPayerForm";
import { SalesContractPayerInformation } from "./SalesContractPayerInformation";
import { disableSalesContractPayerFinantial } from "../../../../../services/salesContractPayerFinantial.service";

const translationPath = "page.4uSalesContract.registers.salesContractPayer.";

export const SalesContractPayer = memo(({ id }: ISalesContractProps) => {
  const { id: salesContractId } = useParams();
  const { onClose, onRefresh } = useSalesContract();
  const { t } = useTranslation();
  const [dataSalesContractPayer, setDataSalesContractPayer] =
    useState<ISalesContractPayer>();

  const [finantialId, setFinantialId] = useState<string>();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createSalesContractPayer
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateSalesContractPayer
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    findSalesContractPayerById
  );

  const { sendRequest: disable } = useFetch(disableSalesContractPayerFinantial);

  const handleEntity = (data: any, type: "QPF" | "QPJ") => {
    if (!data) return null;

    switch (type) {
      case "QPJ":
        return {
          ...data,
          corporateName: data?.corporateName,
        };
      default:
        return {
          ...data,
          individualName: data?.individualName,
        };
    }
  };

  const updateDataHandler = (v: TSalesContractPayerForm, id: string) => {
    return update({
      item: {
        id: id,
        payerTitle: v.payerTitle,
        addressTypeId: v.addressType.id,
        integrationId: v.integrationId || "",
        individualId: v.individual ? v.individual.identifier : undefined,
        legalEntityId: v.legalEntity ? v.legalEntity.id : undefined,
        salesContractId: salesContractId ?? "",
        salesContractPayerFinantial: {
          id: v.idSalesContractPayerFinantial
            ? v.idSalesContractPayerFinantial
            : undefined,
          initialDate: v.initialDate.toISOString().substring(0, 10),
          endDate: v.endDate.toISOString().substring(0, 10),
          monthlyPaymentDueDate: v.monthlyPaymentDueDate,
          mail: v.mail ? v.mail : undefined,
          phone: v.phone ? v.phone : undefined,
          whatsappNumber: v.whatsappNumber ? v.whatsappNumber : undefined,
          observation: v.observation ? v.observation : undefined,
          bilingMethodId: v.bilingMethod ? v.bilingMethod.id : undefined,
        },
      },
      id: id,
    });
  };

  const updateHandler = async (v: TSalesContractPayerForm, id: string) => {
    if (!v.enable) {
      if (!finantialId) return;
      const { success } = await disable(finantialId);
      if (success) {
        return updateDataHandler(v, id);
      } else {
        return { data: null, success: false };
      }
    } else {
      return updateDataHandler(v, id);
    }
  };

  const saveHandler = (v: TSalesContractPayerForm) =>
    save({
      payerTitle: v.payerTitle,
      addressTypeId: v.addressType.id,
      integrationId: v.integrationId || "",
      individualId: v.individual ? v.individual.identifier : undefined,
      legalEntityId: v.legalEntity ? v.legalEntity.id : undefined,
      salesContractId: salesContractId ?? "",
      salesContractPayerFinantial: {
        initialDate: v.initialDate.toISOString().substring(0, 10),
        endDate: v.endDate.toISOString().substring(0, 10),
        monthlyPaymentDueDate: v.monthlyPaymentDueDate,
        mail: v.mail ? v.mail : undefined,
        phone: v.phone ? v.phone : undefined,
        whatsappNumber: v.whatsappNumber ? v.whatsappNumber : undefined,
        observation: v.observation ? v.observation : undefined,
        bilingMethodId: v.bilingMethod ? v.bilingMethod.id : undefined,
      },
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TSalesContractPayerForm>> => {
      const { data, success } = await get(id);

      if (data) {
        setDataSalesContractPayer(data);
        const enabledSalesContractPayerFinantial =
          data.salesContractPayerFinantials
            ? data.salesContractPayerFinantials.find(
                (item) => item.enable === true
              )
            : null;

        setFinantialId(
          enabledSalesContractPayerFinantial
            ? enabledSalesContractPayerFinantial.id
            : undefined
        );
        const newData: TSalesContractPayerForm = {
          ...data,
          code: data.code ? data.code.toString() : "",
          entity: data.legalEntity ? "QPJ" : "QPF",
          individual: handleEntity(data.individual, "QPF"),
          legalEntity: handleEntity(data.legalEntity, "QPJ"),
          idSalesContractPayerFinantial: enabledSalesContractPayerFinantial
            ? enabledSalesContractPayerFinantial.id
            : undefined,
          initialDate: new Date(
            enabledSalesContractPayerFinantial
              ? `${enabledSalesContractPayerFinantial.initialDate}T00:00:00`
              : ""
          ),
          endDate: new Date(
            enabledSalesContractPayerFinantial
              ? `${enabledSalesContractPayerFinantial.endDate}T00:00:00`
              : ""
          ),
          enable: true,
          monthlyPaymentDueDate: enabledSalesContractPayerFinantial
            ? enabledSalesContractPayerFinantial.monthlyPaymentDueDate.toString()
            : "",
          mail: enabledSalesContractPayerFinantial
            ? enabledSalesContractPayerFinantial.mail
            : null,
          phone: enabledSalesContractPayerFinantial
            ? enabledSalesContractPayerFinantial.phone
            : null,
          whatsappNumber: enabledSalesContractPayerFinantial
            ? enabledSalesContractPayerFinantial.whatsappNumber
            : null,
          observation: enabledSalesContractPayerFinantial
            ? enabledSalesContractPayerFinantial.observation
            : null,
          bilingMethod: enabledSalesContractPayerFinantial
            ? enabledSalesContractPayerFinantial.bilingMethod
            : null,
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get]
  );

  const successHandle = () => {
    onClose();
    onRefresh();
  };

  const loading = sLoading || uLoading || gLoading;
  return (
    <FormContainer<TSalesContractPayerForm>
      title={t(translationPath + "title")}
      subtitle=""
      getFunction={getHandler}
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      onSuccess={successHandle}
      showBackButton={false}
      redirectOnSave={false}
      backButtonOnClick={onClose}
      isModalForm={true}
      propId={id}
      loading={loading}
      schema={salesContractPayerSchema}
      marginBottom={0}
      customHeight={"fit-content"}
      formActionsOnBottom={false}
      fields={(type) => (
        <Grid container spacing={2}>
          {type !== "create" && dataSalesContractPayer?.approvalDate ? (
            <SalesContractPayerInformation data={dataSalesContractPayer} />
          ) : (
            <SalesContractPayerForm
              type={type}
              dataSalesContractPayer={dataSalesContractPayer}
              finantialId={finantialId ? finantialId : undefined}
            />
          )}
        </Grid>
      )}
    />
  );
});
